import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

const inputCls =
  "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {done ? (
        <div data-testid="reset-success">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-white">Password reset</h1>
          <p className="mt-3 text-sm text-neutral-400">Redirecting you to sign in…</p>
        </div>
      ) : (
        <>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Set New Password</p>
          <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white">Reset password</h1>
          <p className="mt-2 text-sm text-neutral-500">Choose a new password for your account.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            {!token && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-400">
                Missing or invalid reset token. Please use the link from your email.
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-[#E30613]/30 bg-[#E30613]/10 px-4 py-3 text-sm text-[#FF6069]">{error}</div>
            )}
            <div className="space-y-2">
              <Label className="text-neutral-300">New Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} data-testid="reset-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Confirm Password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={inputCls} data-testid="reset-confirm" />
            </div>
            <button
              type="submit"
              disabled={loading || !token}
              className="flex h-11 w-full items-center justify-center rounded-lg bg-[#E30613] font-medium text-white transition-colors duration-200 hover:bg-[#FF1C2A] disabled:opacity-60"
              data-testid="reset-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
            </button>
          </form>

          <Link to="/login" className="mt-8 inline-block text-sm text-neutral-500 transition-colors hover:text-white">
            ← Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
}
